import { useEffect, useRef, useState } from 'react';
import { Bug, ChevronRight, Wrench } from 'lucide-react';
import { ROLE_NAME, useT } from '../../i18n';
import { fetchMe, type MeResult } from '../../lib/api/me';
import { BugReportFloatingButton, floatingButtonClass } from '../bugs';
import { BugReportModal } from '../bugs/BugReportModal';
import { useTicketCounts } from '../bugs/useTicketCounts';
import { useBugAccess, type BugAccessDeps } from '../bugs/useBugAccess';
import { playerSurfaceActive } from '../../lib/api/customerContext';
import { DevPersonaBar } from '../auth';
import {
  activePersonaKey,
  fetchDevPersonas,
  startDevPersona,
  type DevPersona,
} from '../../lib/api/devPersonas';
import { buildCommit, formatBuildTime } from '../../lib/buildCommit';
import { DevRbacScenario } from './DevRbacScenario';

// ─────────────────────────────────────────────────────────────────────
// THE DEV MENU — one floating button instead of two.
//
// The bug button and the persona switcher were separate pieces of furniture:
// a pill in the bottom-right corner and a bar floating over the page. Both are
// development tooling, and having them in two places meant the persona bar sat
// on top of the product wherever you happened to be.
//
// WHO SEES IT. The same gate the persona switcher already uses: it asks the API
// whether personas exist, and takes the answer as final.
//
// THAT GATE IS BOTH DEPLOYMENT AND ACCOUNT. This file used to claim it was
// «a DEPLOYMENT gate, not an account one», and that a club administrator on the
// dev environment would see the menu too — which was already untrue when it was
// written (#575, review). `apps/api/src/routes/devPersonas.ts` requires BOTH
// `DEV_PERSONAS=enabled`, which env.ts refuses beside a production host, AND a
// caller whose email is `DEV_PERSONAS_ACCOUNT` (default `dev@spoint.no`) or one
// of the closed persona set. Everybody else gets 404 — including that club
// administrator.
//
// SO THERE IS NOTHING TO TIGHTEN HERE, and that is the point worth keeping: the
// identity decision belongs to the server and is already made by the time this
// component sees a list or a 404. Re-checking who is signed in from the bundle
// would be a second, weaker copy of an auth rule, which is how an exception
// eventually gets added to the copy that cannot enforce anything.
//
// WHEN THERE ARE NO PERSONAS IT IS THE OLD BUTTON, unchanged. Ordinary clubs
// keep reporting bugs exactly as before — a «DEV» pill in front of a customer
// would be worse than the duplication this replaces. That fallback is also why
// the component takes over `BugReportButton`'s props rather than wrapping it
// conditionally at the call site: one floating button exists, always, and only
// its dress changes.
// ─────────────────────────────────────────────────────────────────────

export interface DevMenuButtonProps {
  /** Passed through to the bug-report modal, exactly as before. */
  currentView: any;
  currentContext: any;
  onOpenArchive?: any;
  /**
   * The persona switcher's hooks, from the auth gate that owns the session.
   * Absent — the member shell, say — means there is no dev menu to build and
   * this is the ordinary bug button.
   */
  devPersona?: {
    current: string | null;
    onSession: (
      persona: DevPersona,
      session: Awaited<ReturnType<typeof startDevPersona>>,
    ) => void | Promise<void>;
    /**
     * WHO IS SIGNED IN, AND WHERE (asked for 2026-10-19: «det må stå hva slags
     * rolle jeg har»). The account the club session holds and the organization
     * the portal is acting in. The ROLES are not taken from here — the sign-in
     * snapshot goes stale (#817) — but from a fresh `/me` when the menu opens.
     */
    account?: { displayName: string | null; email: string | null } | null;
    scope?: { tenantId: string; name: string } | null;
  };
  /** Injectable for tests — both halves, so a click can be driven without a server. */
  loadPersonas?: typeof fetchDevPersonas;
  startPersona?: typeof startDevPersona;
  /** Injectable for tests: the fresh answer the role line is read from. */
  loadMe?: typeof fetchMe;
  /** Injectable for tests: how the bug controls learn what they may offer. */
  bugAccessDeps?: BugAccessDeps;
  /** Injectable for tests: the RBAC scenario builder's seams (#1024). */
  scenario?: Pick<
    import('./DevRbacScenario').DevRbacScenarioProps,
    'loadCatalog' | 'start' | 'loadMe'
  >;
}

/**
 * «Eier, Turneringsleder» — the roles `/me` reports in one organization, named
 * for a person. Null when the answer has not arrived or does not cover it.
 */
function rolesIn(
  me: MeResult | null,
  tenantId: string | null,
  name: (key: string) => string,
): string[] | null {
  if (!me || me.state !== 'ok' || !tenantId) return null;
  const entry = me.me.authorization.tenants.find((x) => x.tenantId === tenantId);
  return entry ? entry.roles.map(name) : [];
}

export function DevMenuButton({
  currentView,
  currentContext,
  onOpenArchive,
  devPersona,
  loadPersonas = fetchDevPersonas,
  startPersona = startDevPersona,
  loadMe = fetchMe,
  scenario,
  bugAccessDeps,
}: DevMenuButtonProps) {
  const t = useT();
  const [personas, setPersonas] = useState<DevPersona[]>([]);
  const [open, setOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showRights, setShowRights] = useState(false);
  const [showScenario, setShowScenario] = useState(false);
  const [me, setMe] = useState<MeResult | null>(null);
  // WHAT THE BUG CONTROLS MAY OFFER (owner decision 2026-10-22): counts and
  // the archive for a bug administrator, the form for anybody where filing is
  // open, nothing otherwise. Asked once here and handed to the plain button,
  // so the two dresses of the one floating button cannot disagree.
  const bugAccess = useBugAccess(bugAccessDeps);
  const counts = useTicketCounts(showReport, bugAccess.isAdmin);
  const archive = bugAccess.isAdmin ? onOpenArchive : undefined;
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Read once: a bundle cannot change commit while it is running.
  const commit = useRef(buildCommit()).current;

  // THE ROLE LINE IS ASKED FOR EACH TIME THE MENU OPENS, from `/me` — the
  // server's answer now, not the sign-in snapshot, which keeps a revoked role
  // until the next sign-in (#817). Cleared first so a persona switch never
  // shows the previous persona's roles while the new answer is in flight.
  const tenantId = devPersona?.scope?.tenantId ?? null;
  const accountEmail = devPersona?.account?.email ?? null;
  useEffect(() => {
    if (!open || !devPersona) return undefined;
    let live = true;
    setMe(null);
    void loadMe().then((r) => {
      if (live) setMe(r);
    });
    return () => {
      live = false;
    };
    // `devPersona` is a fresh object every render; the account and the
    // organization are what the answer depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loadMe, tenantId, accountEmail, devPersona?.current]);

  useEffect(() => {
    if (!devPersona) return undefined;
    let cancelled = false;
    void loadPersonas()
      .then((list) => {
        if (!cancelled) setPersonas(list);
      })
      // Absent means NO, and a failed read must not conjure a menu.
      //
      // THE 404 IS ABOUT THE CALLER, NOT ONLY THE DEPLOYMENT. This used to be
      // described as deployment gating alone, which undersold it:
      // `apps/api/src/routes/devPersonas.ts` answers for a caller whose email
      // is `DEV_PERSONAS_ACCOUNT` (default `dev@spoint.no`) or one of the
      // closed persona set, and 404s everybody else — so an ordinary club
      // admin signed into the same dev deployment gets no personas and
      // therefore no DEV menu. That is the whole gate, and it already lives on
      // the server: nothing here needs to re-check who is signed in, and
      // adding an identity check in the bundle would be a second, weaker copy
      // of an auth decision (#575, review).
      .catch(() => {
        if (!cancelled) setPersonas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [devPersona, loadPersonas]);

  // Click outside closes it. A menu pinned over the product that only closes
  // via its own button is one people leave open and then complain about.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // No personas, or no session to switch: this is the ordinary bug button and
  // nothing about it changes.
  if (!devPersona || personas.length === 0) {
    return (
      <BugReportFloatingButton
        currentView={currentView}
        currentContext={currentContext}
        onOpenArchive={onOpenArchive}
        access={bugAccess}
      />
    );
  }

  /** One number — open bugs + suggestions together — on the button and in the menu. */
  const totalBadge =
    counts.total > 0 ? (
      <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white font-mono text-[10px] font-bold leading-[18px] text-center">
        {counts.total}
      </span>
    ) : null;

  const item =
    'w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-left text-xs font-semibold text-ink-1 hover:bg-bg-soft/70 transition-colors';

  // WHICH PERSONA THIS ACCOUNT IS — the one switched to in this page's life,
  // or the one whose account is signed in (after a reload or a sign-in).
  const activeKey = activePersonaKey(personas, devPersona.current, accountEmail);
  const activeLabel = personas.find((p) => p.key === activeKey)?.label ?? null;

  const roleName = (key: string) => {
    const k = ROLE_NAME[key];
    return k ? t(k) : key;
  };
  const roles = rolesIn(me, tenantId, roleName);
  const organization = devPersona.scope?.name ?? null;
  const roleLine = (() => {
    if (!organization) return t('dev.menu.noOrganization');
    if (me === null) return t('dev.menu.roleLoading');
    if (roles === null) return t('dev.menu.roleUnknown');
    if (roles.length === 0) return t('dev.menu.noRoleIn', { organization });
    return t('dev.menu.roleIn', { roles: roles.join(', '), organization });
  })();

  return (
    // Above the «new version» banner while it shows (`NEW_BUILD_BANNER_HEIGHT_VAR`).
    <div
      ref={rootRef}
      className="fixed right-4 z-40"
      style={{ bottom: 'calc(1rem + var(--new-build-banner-h, 0px))' }}
    >
      {open && (
        <div
          className="mb-2 w-72 rounded-xl border border-line bg-white shadow-xl p-2"
          role="menu"
          aria-label={t('dev.menu.title')}
        >
          <div className="px-3 pt-1 pb-2 text-[10px] font-mono uppercase tracking-wide text-ink-3">
            {t('dev.menu.lead')}
          </div>

          {/* WHO YOU ARE, AND WHAT YOU MAY DO HERE (2026-10-19). The persona
              names say which TEST ACCOUNT is signed in; they do not say which
              ROLE it holds — «Klubbadmin» is Eier — and after a reload no
              persona was marked at all. So the menu says it in words: the
              account, the persona it is, and the roles in this organization
              as the server reports them now. */}
          <div className="px-3 pb-2 mb-1 border-b border-line" data-testid="dev-menu-whoami">
            <div className="text-[11px] text-ink-1 leading-snug">
              {t('dev.menu.signedInAs', {
                name:
                  devPersona.account?.displayName ??
                  devPersona.account?.email ??
                  t('dev.menu.unknownAccount'),
              })}
              {activeLabel ? <span className="text-ink-3"> · {activeLabel}</span> : null}
            </div>
            <div className="mt-0.5 text-[11px] text-ink-2 leading-snug" data-testid="dev-menu-role">
              {roleLine}
            </div>
          </div>

          {/* WHICH CHANGE IS LIVE. The question this menu gets asked most on a
              dev deployment, and the one it could not answer: the bundle knew
              its build id, which is a hash for comparing and not a sentence.
              A commit subject carries the squash-merge's PR number, and the
              committer date is when the change landed. */}
          <div className="px-3 pb-2 mb-1 border-b border-line">
            {commit ? (
              <>
                <div className="text-[11px] text-ink-1 leading-snug">
                  {commit.pr ? (
                    <a
                      href={`https://github.com/Starpoint-AS/starpoint-app/pull/${commit.pr}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold underline decoration-dotted"
                    >
                      #{commit.pr}
                    </a>
                  ) : (
                    <span className="font-semibold">{commit.sha}</span>
                  )}{' '}
                  <span className="text-ink-2">{commit.subject}</span>
                </div>
                <div className="mt-0.5 text-[10px] font-mono text-ink-3">
                  {commit.sha}
                  {commit.at ? ` · ${formatBuildTime(commit.at)}` : ''}
                </div>
              </>
            ) : (
              // A build with no git to ask. Said plainly rather than left
              // blank, which would read as a broken menu.
              <div className="text-[11px] text-ink-3">{t('dev.menu.buildUnknown')}</div>
            )}
          </div>

          <button className={item} onClick={() => setShowRights((v) => !v)} role="menuitem">
            <span className="flex items-center gap-2">
              <Wrench size={13} strokeWidth={2.5} />
              {t('dev.menu.rights')}
            </span>
            <ChevronRight
              size={13}
              className={showRights ? 'rotate-90 transition-transform' : 'transition-transform'}
            />
          </button>
          {showRights && (
            <div className="px-2 py-2">
              {/* v2 (#1024): any organizations, any of their roles, on an
                  isolated dev identity. Opens over the page — a matrix of
                  organizations and roles does not fit in a menu. */}
              <button
                type="button"
                className={`${item} mb-1`}
                onClick={() => {
                  setShowScenario(true);
                  setOpen(false);
                }}
                data-testid="dev-rbac-open"
              >
                {t('dev.rbac.open')}
              </button>
              {/* v1's four fixed personas, kept as one-click presets. */}
              <div className="px-3 pt-1 pb-0.5 text-[10px] font-mono uppercase tracking-wide text-ink-3">
                {t('dev.rbac.presets')}
              </div>
              {/* The switcher itself, not a second copy of it. `load` is its own
                  injectable seam, used here so the list is fetched once for the
                  menu and the bar rather than twice on every open. */}
              <DevPersonaBar
                variant="list"
                current={activeKey}
                onSession={devPersona.onSession}
                load={() => Promise.resolve(personas)}
                start={startPersona}
              />
            </div>
          )}

          {bugAccess.canReport && (
            <button
              className={item}
              onClick={() => {
                setShowReport(true);
                setOpen(false);
              }}
              role="menuitem"
            >
              <span className="flex items-center gap-2">
                <Bug size={13} strokeWidth={2.5} />
                {t('dev.menu.report')}
              </span>
            </button>
          )}

          {archive && (
            <button
              className={item}
              onClick={() => {
                archive();
                setOpen(false);
              }}
              role="menuitem"
            >
              <span className="flex items-center gap-2">{t('dev.menu.archive')}</span>
              {totalBadge}
            </button>
          )}
        </div>
      )}

      {/* SMALL ON PURPOSE. One icon and, when something is open, one number —
          the split by kind lives in the tooltip and the archive. With nothing
          open it is a small light disc — visible, but out of the way. */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t('dev.menu.title')}
        className={floatingButtonClass(counts.total)}
        title={
          counts.total > 0
            ? t('dev.menu.titleCounts', {
                bugs: String(counts.bugs),
                features: String(counts.features),
              })
            : t('dev.menu.titleEmpty')
        }
      >
        <Wrench size={13} strokeWidth={2.5} />
        {totalBadge}
      </button>

      {showScenario && (
        <DevRbacScenario
          onClose={() => setShowScenario(false)}
          onSession={devPersona.onSession}
          accountEmail={accountEmail}
          {...scenario}
        />
      )}

      {showReport && (
        <BugReportModal
          currentView={currentView}
          currentContext={currentContext}
          onClose={() => setShowReport(false)}
          onOpenArchive={archive}
          mode={playerSurfaceActive() ? 'member' : 'staff'}
        />
      )}
    </div>
  );
}
